import type {
  Color,
  Body as BodyNode,
  Geom,
  Mesh,
  BodyUUID,
  MeshUUID,
  MaterialUUID,
  UUID,
} from "../../types/scene";
import { create } from "mutative";
import { withHistory } from "./withHistory";
import { generateUuid } from "../../utils/id";
import { ensureDefaultMatMaterialEntry } from "../../utils/materialDefaults";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";
import { logger } from "../../utils/logger";
// Note: getMeshColor internally uses sceneObjects from viewer
// but doesn't import from viewer to avoid circular dependency

import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { MeshActions, MeshInput } from "./actionTypes";

const MIN_MESH_SCALE = 1e-6;

const toBodyUuid = (uuid: string): BodyUUID => uuid as BodyUUID;
const toMeshUuid = (uuid: string): MeshUUID => uuid as MeshUUID;
const toMaterialUuid = (uuid: string): MaterialUUID => uuid as MaterialUUID;

const sanitizeMeshScale = (scale: [number, number, number]): [number, number, number] => {
  const next = [0, 1, 2].map((idx) => {
    const n = Number(scale?.[idx]);
    if (!Number.isFinite(n)) return 1;
    return Math.max(MIN_MESH_SCALE, n);
  }) as [number, number, number];
  return next;
};

const sanitizeMeshReferencePosition = (pos: [number, number, number]): [number, number, number] => {
  const next = [0, 1, 2].map((idx) => {
    const n = Number(pos?.[idx]);
    if (Number.isFinite(n)) return n;
    logger.warn("[meshActions] Invalid mesh reference position component; using 0.", {
      index: idx,
      value: pos?.[idx],
    });
    return 0;
  }) as [number, number, number];
  return next;
};

const normalizeMeshReferenceQuat = (
  quat: [number, number, number, number]
): [number, number, number, number] => {
  const next = [0, 1, 2, 3].map((idx) => {
    const fallback = idx === 0 ? 1 : 0;
    const n = Number(quat?.[idx]);
    if (Number.isFinite(n)) return n;
    logger.warn("[meshActions] Invalid mesh reference quaternion component; using fallback.", {
      index: idx,
      value: quat?.[idx],
      fallback,
    });
    return fallback;
  }) as [number, number, number, number];
  const norm = Math.hypot(next[0], next[1], next[2], next[3]);
  if (!Number.isFinite(norm) || norm < 1e-10) {
    logger.warn("[meshActions] Invalid mesh reference quaternion length; using identity.");
    return [1, 0, 0, 0];
  }
  return [next[0] / norm, next[1] / norm, next[2] / norm, next[3] / norm];
};

const vectorsEqual = (a: unknown, b: number[], epsilon = 1e-12): boolean =>
  Array.isArray(a) &&
  a.length === b.length &&
  b.every((value, idx) => Math.abs((Number(a[idx]) || 0) - value) <= epsilon);

const getUniformBodyPrimitiveMaterialId = (
  body: BodyNode | null | undefined,
  materials: Record<string, unknown>
): MaterialUUID | null => {
  if (!body || !Array.isArray(body.geoms)) return null;
  const visualMeshCount = body.geoms.filter((g) => g && g.mesh && g.visualOnly === true).length;
  if (visualMeshCount > 0) return null;

  const visualPrimitives = body.geoms.filter((g) => g && !g.mesh && g.visualOnly === true);
  if (visualPrimitives.length === 0) return null;

  const materialIds = Array.from(
    new Set(
      visualPrimitives
        .map((g) => (Array.isArray(g.materials) ? g.materials[0] : null))
        .filter((id): id is MaterialUUID => !!id && !!materials[id as MaterialUUID])
    )
  ) as MaterialUUID[];

  return materialIds.length === 1 ? materialIds[0] : null;
};

const clearBodyPrimitiveVisualMaterials = (body: BodyNode | null | undefined) => {
  if (!body || !Array.isArray(body.geoms)) return;
  body.geoms = body.geoms.map((geom) => {
    if (!geom || geom.mesh || geom.visualOnly !== true) return geom;
    const next = { ...geom };
    delete (next as Partial<Geom>).materials;
    return next as Geom;
  });
};

// MeshColor is a number array [r, g, b, a]
type ThreeColorLike = Color | number[] | { r: number; g: number; b: number };
type ThreeObject = {
  name?: string;
  traverse?: (callback: (obj: ThreeObject) => void) => void;
  material?: { color?: ThreeColorLike };
};
type SceneObjectRegistry = Record<string, ThreeObject | undefined>;
type SceneObjectsWindow = Window & { getSceneObjects?: () => SceneObjectRegistry };

const formatThreeColor = (color: ThreeColorLike): Color | undefined => {
  if (Array.isArray(color)) {
    const [r, g, b, a = 1] = color;
    return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})` as Color;
  }
  if (typeof color === "object" && "r" in color && "g" in color && "b" in color) {
    return `rgb(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)})` as Color;
  }
  return undefined;
};

export const createMeshActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): MeshActions => ({
  getMeshColor: (meshUuid: string): Color | undefined => {
    const state = get();
    // Look up mesh by UUID to get its name for Three.js object lookup
    const mesh = state.meshes?.[meshUuid as MeshUUID];
    const meshName = mesh?.name;

    if (!meshUuid || !meshName) {
      return undefined;
    }

    // window.getSceneObjects is set by the viewer
    const sceneWindow = typeof window !== "undefined" ? (window as SceneObjectsWindow) : undefined;
    if (typeof sceneWindow?.getSceneObjects !== "function") {
      return undefined;
    }

    const objects = sceneWindow.getSceneObjects();
    if (!objects) {
      return undefined;
    }

    // First check if this mesh exists directly in sceneObjects (by name)
    if (objects[meshName]) {
      const obj = objects[meshName] as ThreeObject;
      if (obj && obj.material && obj.material.color) {
        return formatThreeColor(obj.material.color);
      }
    }

    // If not found directly, try to find it in the scene hierarchy
    for (const [, obj] of Object.entries(objects)) {
      if (obj && typeof obj === "object" && "traverse" in obj) {
        let found: Color | undefined;
        (obj as ThreeObject).traverse?.((child) => {
          if (found) return;
          if (child.name === meshName && child.material && child.material.color) {
            found = formatThreeColor(child.material.color);
          }
        });
        if (found) return found;
      }
    }

    return undefined;
  },

  moveMeshToBody: withHistory((meshUuid: string, targetBodyUuid: string) =>
    set((state) =>
      create(state, (draft) => {
        const defaultMaterialId = ensureDefaultMatMaterialEntry(draft.materials);
        // Get mesh name for geom creation
        const mesh = draft.meshes?.[toMeshUuid(meshUuid)];
        const meshName = mesh?.name || meshUuid;

        // Find source body: iterate Object.values(bodies) to find which body has a geom with the mesh
        let sourceBody: BodyNode | null = null;
        let meshMaterial = defaultMaterialId as MaterialUUID;

        for (const body of Object.values(draft.bodies) as BodyNode[]) {
          if (!body || !body.geoms) continue;
          const geomWithMesh = body.geoms.find((g) => g && g.mesh === meshUuid);
          if (geomWithMesh) {
            sourceBody = body;
            // Preserve material from source geom
            const candidate = geomWithMesh.materials?.[0];
            if (candidate && draft.materials[candidate as MaterialUUID]) {
              meshMaterial = candidate as MaterialUUID;
            }
            break;
          }
        }

        // Remove mesh from source body
        if (sourceBody && sourceBody.geoms) {
          sourceBody.geoms = sourceBody.geoms.filter((geom) => geom.mesh !== meshUuid);
        }

        // Find target body: direct lookup by UUID
        const targetBody = draft.bodies[toBodyUuid(targetBodyUuid)];
        if (!targetBody) {
          logger.warn(`[moveMeshToBody] Target body "${targetBodyUuid}" not found`);
          return;
        }

        // If this body previously used a single object-level primitive material,
        // inherit it into the first visual mesh and then clear primitive material refs.
        const inheritedObjectMaterialId = getUniformBodyPrimitiveMaterialId(
          targetBody,
          draft.materials
        );
        if (inheritedObjectMaterialId) {
          meshMaterial = inheritedObjectMaterialId;
        }

        // Add mesh to target body with preserved material
        targetBody.geoms = targetBody.geoms || [];
        const existingGeomIndex = targetBody.geoms.findIndex((g) => g.mesh === meshUuid);
        if (existingGeomIndex >= 0) {
          targetBody.geoms[existingGeomIndex].materials = [meshMaterial];
          targetBody.geoms[existingGeomIndex].kind = "mesh";
          targetBody.geoms[existingGeomIndex].visualOnly = true;
        } else {
          targetBody.geoms.push({
            uuid: generateUuid() as UUID,
            name: meshName,
            kind: "mesh",
            position: [0, 0, 0],
            orientation: [1, 0, 0, 0],
            size: [1, 1, 1],
            mesh: toMeshUuid(meshUuid),
            materials: [meshMaterial],
            visualOnly: true,
          });
        }

        if (inheritedObjectMaterialId) {
          clearBodyPrimitiveVisualMaterials(targetBody);
        }
      })
    )
  ),

  getMeshParentBody: (meshUuid: string): string | null => {
    const state = get();
    const bodiesMap = state.bodies;

    // Direct flat map search - check which body has a geom with this mesh
    for (const body of Object.values(bodiesMap)) {
      if (!body || !body.geoms) continue;
      if (body.geoms.some((g) => g && g.mesh === meshUuid)) {
        return body.name;
      }
    }

    return null;
  },

  setMeshName: withHistory((meshUuid: string, name: string) =>
    set((state) =>
      create(state, (draft) => {
        const mesh = draft.meshes?.[toMeshUuid(meshUuid)];
        if (!mesh) return;
        mesh.name = name;
      })
    )
  ),

  // Set the material for every visual geom that references this mesh asset.
  setMeshMaterial: withHistory((meshUuid: string, newMaterialId: string) =>
    set((state) => {
      let resolvedMaterialIdForEvent: string | null = null;
      let meshNameForEvent: string | null = null;
      let changed = false;

      const nextState = create(state, (draft) => {
        const resolvedMaterialId =
          newMaterialId && draft.materials[toMaterialUuid(newMaterialId)]
            ? toMaterialUuid(newMaterialId)
            : (ensureDefaultMatMaterialEntry(draft.materials) as MaterialUUID);
        resolvedMaterialIdForEvent = resolvedMaterialId;

        // Get mesh name for geom creation
        const mesh = draft.meshes?.[toMeshUuid(meshUuid)];
        const meshName = mesh?.name || meshUuid;
        meshNameForEvent = meshName;

        // Direct flat map update - update every body using this mesh. A mesh asset can be instanced.
        for (const body of Object.values(draft.bodies) as BodyNode[]) {
          if (!body) continue;
          body.geoms = body.geoms || [];

          if (body.geoms && body.geoms.some((geom) => geom.mesh === meshUuid)) {
            body.geoms = body.geoms.map((geom) => {
              if (geom.mesh === meshUuid) {
                changed = true;
                return {
                  ...geom,
                  materials: [resolvedMaterialId],
                  kind: "mesh",
                  visualOnly: geom.visualOnly ?? true,
                } satisfies Geom;
              }
              return geom;
            });
          }
        }
      });

      if (changed && typeof window !== "undefined") {
        try {
          window.dispatchEvent(
            new CustomEvent("material-assignment-changed", {
              detail: {
                meshName: meshUuid,
                material: resolvedMaterialIdForEvent,
              },
            })
          );
          if (meshNameForEvent && meshNameForEvent !== meshUuid) {
            window.dispatchEvent(
              new CustomEvent("material-assignment-changed", {
                detail: {
                  meshName: meshNameForEvent,
                  material: resolvedMaterialIdForEvent,
                },
              })
            );
          }
          window.dispatchEvent(new CustomEvent("structure-changed"));
        } catch (error) {
          logger.warn("[meshActions] Failed to dispatch material assignment change", error);
        }
      }

      return nextState;
    })
  ),

  removeMesh: withHistory((meshIdentifier: string) => {
    set((state) =>
      create(state, (draft) => {
        if (!meshIdentifier) return;

        // Accept either UUID or mesh name.
        let resolvedMeshUuid = meshIdentifier;
        if (!draft.meshes?.[toMeshUuid(resolvedMeshUuid)]) {
          const found = Object.entries(draft.meshes || {}).find(
            ([, mesh]) => mesh?.name === meshIdentifier
          );
          if (found) resolvedMeshUuid = String(found[0]);
        }

        // Remove all geoms that reference this mesh (UUID-first, name fallback).
        for (const body of Object.values(draft.bodies || {}) as BodyNode[]) {
          if (!body || !Array.isArray(body.geoms)) continue;
          body.geoms = body.geoms.filter((geom) => {
            const ref = geom?.mesh;
            return ref !== resolvedMeshUuid && ref !== meshIdentifier;
          });
        }

        // Remove mesh metadata entry when present.
        if (draft.meshes && draft.meshes[toMeshUuid(resolvedMeshUuid)]) {
          delete draft.meshes[toMeshUuid(resolvedMeshUuid)];
        }
      })
    );

    // Ensure viewer rebuilds immediately after mesh removal.
    try {
      window.dispatchEvent(new CustomEvent("structure-changed"));
    } catch (error) {
      logger.warn("[meshActions] Failed to dispatch structure-changed after removeMesh", error);
    }
  }),

  addMesh: withHistory((meshInput: MeshInput): MeshUUID => {
    const mesh = { ...meshInput };
    if (!mesh.uuid) {
      mesh.uuid = generateUuid() as MeshUUID;
    }

    set((state) =>
      create(state, (draft) => {
        draft.meshes = draft.meshes || {};
        mesh.name = nextUniqueName(mesh.name || "mesh", namesFromRecord(draft.meshes));
        draft.meshes[mesh.uuid as MeshUUID] = mesh as Mesh;
      })
    );
    return mesh.uuid as MeshUUID;
  }),

  setMeshReferenceTransform: withHistory(
    (meshUuid: string, pos: [number, number, number], quat: [number, number, number, number]) => {
      const nextPos = sanitizeMeshReferencePosition(pos);
      const nextQuat = normalizeMeshReferenceQuat(quat);
      let changed = false;

      set((state) =>
        create(state, (draft) => {
          const mesh = draft.meshes?.[toMeshUuid(meshUuid)];
          if (!mesh) return;
          if (
            vectorsEqual(mesh.referencePosition, nextPos) &&
            vectorsEqual(mesh.referenceOrientation, nextQuat)
          ) {
            return;
          }
          changed = true;
          mesh.referencePosition = nextPos;
          mesh.referenceOrientation = nextQuat;
        })
      );

      if (changed) {
        try {
          window.dispatchEvent(new CustomEvent("structure-changed"));
        } catch (error) {
          logger.warn(
            "[meshActions] Failed to dispatch structure-changed after setMeshReferenceTransform",
            error
          );
        }
      }
    }
  ),

  setMeshScale: withHistory((meshUuid: string, scale: [number, number, number]) => {
    set((state) =>
      create(state, (draft) => {
        const mesh = draft.meshes?.[toMeshUuid(meshUuid)];
        if (!mesh) return;
        mesh.scale = sanitizeMeshScale(scale);
      })
    );

    try {
      window.dispatchEvent(new CustomEvent("structure-changed"));
    } catch (error) {
      logger.warn("[meshActions] Failed to dispatch structure-changed after setMeshScale", error);
    }
  }),

  updateMeshUrl: withHistory(
    (meshUuid: string, url: string, name?: string, fingerprint?: string) => {
      set((state) =>
        create(state, (draft) => {
          const mesh = draft.meshes?.[toMeshUuid(meshUuid)];
          if (!mesh) return;
          mesh.url = url;
          if (name !== undefined) mesh.name = name;
          if (fingerprint !== undefined) mesh.fingerprint = fingerprint;
          else delete mesh.fingerprint;
        })
      );
      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[meshActions] Failed to dispatch structure-changed after updateMeshUrl",
          error
        );
      }
    }
  ),
});
